import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import TenantMetricsStrip from './components/TenantMetricsStrip';
import FilterHeader from './components/FilterHeader';
import SubscriptionDistributionChart from './components/SubscriptionDistributionChart';
import TenantHealthLeaderboard from './components/TenantHealthLeaderboard';
import TenantDataTable from './components/TenantDataTable';
import TenantDetailModal from './components/TenantDetailModal';
import { supabase } from 'components/supabase/supabaseClient';

const TenantManagementAnalytics = () => {
  const [filters, setFilters] = useState({
    subscriptionPlans: [],
    tenantStatus: [],
    regions: []
  });
  const [comparisonMode, setComparisonMode] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  // Auto-refresh data every 15 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      setLastRefresh(new Date());
    }, 15 * 60 * 1000); // 15 minutes

    return () => clearInterval(interval);
  }, []);

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleComparisonToggle = () => {
    setComparisonMode(prev => !prev);
  };

  const handleTenantDrillDown = (planType) => {
    console.log('Drilling down into plan:', planType);
    // Filter table by plan type or show detailed breakdown
  };

  const handleTenantSelect = (tenant) => {
    setSelectedTenant(tenant);
    setModalOpen(true);
  };

  const handleActionClick = (action, tenant) => {
    switch (action) {
      case 'view':
        handleTenantSelect(tenant);
        break;
      case 'edit': console.log('Edit tenant:', tenant.id);
        break;
      case 'more': console.log('More actions for tenant:', tenant.id);
        break;
      default:
        break;
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedTenant(null);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <Header />
      
      {/* Sidebar */}
      <Sidebar />
      
      {/* Main Content */}
      <main className="lg:ml-60 pt-16 pb-20 lg:pb-8">
        <div className="p-6 space-y-6">
          {/* Page Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            <div>
              <h1 className="text-2xl font-bold text-text-primary">
                Tenant Management Analytics
              </h1>
              <p className="text-text-secondary mt-1">
                Monitor tenant health, track subscription lifecycles, and identify growth opportunities
              </p>
            </div>
            
            <div className="flex items-center space-x-2 text-sm text-text-secondary">
              <span>Last updated: {lastRefresh.toLocaleTimeString()}</span>
              <div className="w-2 h-2 bg-success rounded-full animate-pulse" />
            </div>
          </div>

          {/* Filter Header */}
          <FilterHeader
            onFiltersChange={handleFiltersChange}
            onComparisonToggle={handleComparisonToggle}
            comparisonMode={comparisonMode}
          />

          {/* Metrics Strip */}
          <TenantMetricsStrip />

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
            {/* Subscription Distribution Chart */}
            <div className="xl:col-span-8">
              <SubscriptionDistributionChart onTenantDrillDown={handleTenantDrillDown} />
            </div>

            {/* Tenant Health Leaderboard */}
            <div className="xl:col-span-4">
              <TenantHealthLeaderboard onTenantSelect={handleTenantSelect} />
            </div>
          </div>

          {/* Tenant Data Table */}
          <TenantDataTable
            onTenantSelect={handleTenantSelect}
            onActionClick={handleActionClick}
          />

          {/* Batch Processing Indicator */}
          {comparisonMode && (
            <div className="fixed bottom-4 right-4 bg-primary text-primary-foreground px-4 py-2 rounded-lg shadow-elevation-3 flex items-center space-x-2">
              <div className="w-2 h-2 bg-primary-foreground rounded-full animate-pulse" />
              <span className="text-sm font-medium">Comparison mode active</span>
            </div>
          )}
        </div>
      </main>

      {/* Tenant Detail Modal */}
      <TenantDetailModal
        tenant={selectedTenant}
        isOpen={modalOpen}
        onClose={handleModalClose}
      />
    </div>
  );
};

export default TenantManagementAnalytics;